import { useEffect, useState } from 'react';
import {
  Alert, Button, CircularProgress, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, Stack, Switch, Typography,
} from '@mui/material';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import PointOfSaleOutlined from '@mui/icons-material/PointOfSaleOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { getTenantModules, setTenantModules } from './api';
import type { ModuleKey, ModuleState, ModulesStatus, TenantModulesResponse } from './api';
import { ApiError } from '@/lib/api';
import { apiErrorMessage } from '@/lib/errors';
import { GlassDialog } from '@/components/GlassDialog';

const MODULES: { key: ModuleKey; label: string; hint: string; Icon: SvgIconComponent }[] = [
  { key: 'sale', label: 'ຂາຍ', hint: 'ໃບສະເໜີລາຄາ, ໃບບິນຂາຍ, ໃບສົ່ງສິນຄ້າ', Icon: StorefrontOutlined },
  { key: 'purchase', label: 'ຈັດຊື້', hint: 'ໃບສັ່ງຊື້, ຮັບສິນຄ້າເຂົ້າ', Icon: ShoppingCartOutlined },
  { key: 'warehouse', label: 'ສາງສິນຄ້າ', hint: 'ສະຕັອກ, ລ໋ອດ, ໂອນຍ້າຍສິນຄ້າ', Icon: Inventory2Outlined },
  { key: 'pos', label: 'POS', hint: 'ຂາຍໜ້າຮ້ານ, ກະ ແລະ ບິນ POS', Icon: PointOfSaleOutlined },
];

const LABEL_OF: Record<ModuleKey, string> = Object.fromEntries(
  MODULES.map(({ key, label }) => [key, label]),
) as Record<ModuleKey, string>;

/** Why one module cannot be switched, shown in place of its hint. */
const NOT_UPGRADED = 'ຖານຂໍ້ມູນຂອງບໍລິສັດນີ້ຍັງບໍ່ມີໂມດູນນີ້';

/** The whole-tenant read-only states, each with the remedy that applies to it.
 *  They are separate because only one of them heals on its own. */
const BLOCKED = {
  unavailable: {
    severity: 'info' as const,
    message: 'ບໍລິສັດນີ້ຍັງບໍ່ທັນອັບເດດຖານຂໍ້ມູນ — ຈະອັບເດດອັດຕະໂນມັດເມື່ອມີຄົນ login '
      + 'ເຂົ້າບໍລິສັດນີ້ຄັ້ງທຳອິດຫຼັງຈາກ deploy ແລ້ວຈຶ່ງຈະຕັ້ງຄ່ານີ້ໄດ້',
  },
  unprovisioned: {
    severity: 'warning' as const,
    message: 'ຖານຂໍ້ມູນຂອງບໍລິສັດນີ້ບໍ່ມີແຖວ setting — ການສ້າງບໍລິສັດອາດຂາດຕອນ. '
      + 'ການ login ຈະບໍ່ແກ້ໃຫ້ ຕ້ອງໃຫ້ຜູ້ດູແລລະບົບແກ້ດ້ວຍມື',
  },
};

// `Object.hasOwn`, not `in`: `in` walks the prototype chain, so a status of
// 'toString' or 'constructor' would read as blocked.
const readOnly = (s: ModulesStatus): s is keyof typeof BLOCKED => Object.hasOwn(BLOCKED, s);

type Choice = Record<ModuleKey, boolean>;
const choiceFrom = (modules: Record<ModuleKey, ModuleState>): Choice =>
  Object.fromEntries(MODULES.map(({ key }) => [key, modules[key] === 'on'])) as Choice;

/** The modules whose switch no longer matches what is stored. Modules the
 *  tenant has no column for are never included — their switch cannot move. */
function changedKeys(current: TenantModulesResponse, choice: Choice): ModuleKey[] {
  return MODULES
    .map(({ key }) => key)
    .filter((key) => current.modules[key] !== 'unavailable' && choice[key] !== (current.modules[key] === 'on'));
}

/** The save is rejected as a whole when it names a module the tenant lacks —
 *  reachable if the tenant's schema changed since the dialog loaded. Naming the
 *  modules matters: the rest of the same save did not land either. */
function saveErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const body = JSON.parse(err.message) as { error?: string; modules?: ModuleKey[] };
      if (body.error === 'tenant_not_upgraded' && body.modules?.length) {
        const names = body.modules.map((m) => LABEL_OF[m] ?? m).join(', ');
        return `ຖານຂໍ້ມູນຂອງບໍລິສັດນີ້ຍັງບໍ່ມີໂມດູນ ${names} — ບໍ່ໄດ້ບັນທຶກຢ່າງໃດເລີຍ`;
      }
    } catch {
      // body wasn't JSON — fall through to the shared message table
    }
  }
  return apiErrorMessage(err, 'ບັນທຶກບໍ່ສຳເລັດ');
}

export function ModulesDialog({
  companyId, companyName, open, onClose, basePath = '/api/companies',
}: {
  companyId: number | null;
  companyName?: string;
  open: boolean;
  onClose: () => void;
  basePath?: string;
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tagged with the company it was loaded for: the dialog is reused across rows,
  // so an untagged value would let the previous company's state (and the
  // operator's unsaved switches) survive into — and be PUT to — the next one.
  const [loaded, setLoaded] = useState<
    { id: number; current: TenantModulesResponse; choice: Choice } | null
  >(null);
  const view = loaded !== null && loaded.id === companyId ? loaded : null;

  useEffect(() => {
    const id = companyId;
    if (!open || id == null) return;
    let active = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await getTenantModules(id as number, basePath);
        if (!active) return;
        setLoaded({ id: id as number, current: res, choice: choiceFrom(res.modules) });
      } catch {
        if (active) {
          setError('ບໍ່ສາມາດໂຫຼດຄ່າໄດ້');
          // Nothing was read, so nothing may be shown or sent. Without this the
          // same company's previous opening — including unsaved switches —
          // stays in state and reads as loaded once `loading` clears.
          setLoaded(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => { active = false; };
  }, [open, companyId, basePath]);

  const blocked = view !== null && readOnly(view.current.status) ? BLOCKED[view.current.status as keyof typeof BLOCKED] : null;
  const changed = view !== null && blocked === null ? changedKeys(view.current, view.choice) : [];
  // `loading` matters: on a reopen the previous opening's unsaved switches are
  // still in state while the fresh value is being read, and saving them would
  // write values the operator never confirmed against what is stored now.
  const dirty = !loading && changed.length > 0;
  const anyUnavailable = view !== null && blocked === null
    && MODULES.some(({ key }) => view.current.modules[key] === 'unavailable');

  function toggle(key: ModuleKey, value: boolean) {
    setLoaded((l) => (l !== null && l.id === companyId ? { ...l, choice: { ...l.choice, [key]: value } } : l));
  }

  async function onSave() {
    if (companyId == null || view === null || !dirty) return;
    const body = Object.fromEntries(changed.map((key) => [key, view.choice[key]]));
    setSaving(true);
    try {
      await toast.promise(
        setTenantModules(companyId, body, basePath),
        { loading: 'ກຳລັງບັນທຶກ...', success: 'ບັນທຶກສຳເລັດ', error: saveErrorMessage },
      );
      // Only the modules that were sent moved; the rest keep whatever the read
      // reported, including 'unavailable'.
      setLoaded((l) => (l !== null && l.id === companyId
        ? {
          ...l,
          current: {
            ...l.current,
            modules: { ...l.current.modules, ...Object.fromEntries(changed.map((k) => [k, view.choice[k] ? 'on' : 'off'])) },
          },
        }
        : l));
      onClose();
    } catch {
      // toast.promise already displayed the error
    } finally {
      setSaving(false);
    }
  }

  return (
    <GlassDialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        ໂມດູນທີ່ເປີດໃຊ້
        {companyName && (
          <Typography variant="body2" color="text.secondary">{companyName}</Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <CircularProgress size={28} sx={{ display: 'block', mx: 'auto', my: 3 }} />
        ) : view === null ? (
          // Nothing loaded for this company — the error Alert above says why.
          // Switches here would paint "off" as a stored value nobody read.
          null
        ) : blocked !== null ? (
          <Alert severity={blocked.severity}>{blocked.message}</Alert>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {MODULES.map(({ key, label, hint, Icon }) => {
              const unavailable = view.current.modules[key] === 'unavailable';
              return (
                <FormControlLabel
                  key={key}
                  // The whole row is the target, and the label stays the
                  // switch's accessible name.
                  labelPlacement="start"
                  sx={{ ml: 0, mr: 0, py: 1, justifyContent: 'space-between' }}
                  disabled={unavailable}
                  control={
                    <Switch
                      checked={view.choice[key]}
                      onChange={(e) => toggle(key, e.target.checked)}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                      <Icon fontSize="small" color={unavailable ? 'disabled' : 'action'} />
                      <Stack>
                        <Typography variant="body2">{label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {unavailable ? NOT_UPGRADED : hint}
                        </Typography>
                      </Stack>
                    </Stack>
                  }
                />
              );
            })}
          </Stack>
        )}
        {anyUnavailable && (
          // Said once rather than on every greyed row: the remedy is the same
          // for all of them.
          <Alert severity="info" sx={{ mt: 2 }}>
            ໂມດູນທີ່ເປັນສີຈາງຍັງບໍ່ມີໃນຖານຂໍ້ມູນຂອງບໍລິສັດນີ້ — ຈະເພີ່ມໃຫ້ອັດຕະໂນມັດເມື່ອມີຄົນ login ເຂົ້າບໍລິສັດນີ້ຄັ້ງຕໍ່ໄປ
          </Alert>
        )}
        {view !== null && blocked === null && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            ປິດໂມດູນແມ່ນພຽງແຕ່ເຊື່ອງເມນູໃນລະບົບບັນຊີ ບໍ່ໄດ້ລຶບຂໍ້ມູນທີ່ມີຢູ່
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ປິດ</Button>
        <Button variant="contained" disabled={!dirty || saving} onClick={() => void onSave()}>ບັນທຶກ</Button>
      </DialogActions>
    </GlassDialog>
  );
}
